// import React from "react";
import { Box, Button, Card, Typography } from "@mui/material";
// import { useWindowSize } from "../hooks/useWindowSize";
import { workshopCardStyle } from "../../../../shared/styles/cardStyles";
import { useDispatch, useSelector } from "react-redux";
import { setDraft } from '../../../../app/store/reservationSlice';
import { DayPicker } from "react-day-picker";
import { enUS, fi, sv } from "date-fns/locale";
import "react-day-picker/style.css";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "../../../../shared/hooks/useWindowSize";
import Holidays from "date-holidays"
import "../../../../shared/styles/style.css"
import { useCallback, useEffect, useState } from "react";

dayjs.extend(isSameOrBefore);

export default function WorkshopCalendar() {
    // const { isMobile } = useWindowSize();
    const dispatch = useDispatch();
    const draft = useSelector((state) => state.reservation.draft);
    const { i18n, t } = useTranslation();
    const { isDesktop, isTablet } = useWindowSize();
    const queueSize = draft?.workshop?.queueSize || 3
    const holidays = new Holidays('FI');

    // Map i18n language to date-fns locale
    const localeMap = {
        fi: fi,
        sv: sv,
        en: enUS
    };
    const calendarLocale = localeMap[i18n.language] || enUS;

    // Use dayjs for today
    const today = dayjs().startOf('day');
    const currentMonth = dayjs().startOf("month").toDate();
    const [month, setMonth] = useState(currentMonth);
    let disableButton = dayjs(month).isSame(currentMonth, 'month')
    const handleCurrentMonth = () => {
        setMonth(dayjs().startOf("month").toDate());
    };

    // Disable weekends function
    const isWeekend = useCallback((date) => {
        const day = date.getDay();
        return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
    }, []);

    // Check if date is a holiday
    const isHoliday = useCallback((date) => {
        const year = dayjs(date).year();

        const publicHolidays = holidays.getHolidays(year).filter(holiday => holiday.type === 'public');

        return publicHolidays.some(
            holiday => dayjs(holiday.date).format('YYYY-MM-DD') === dayjs(date).format('YYYY-MM-DD')
        );
    }, [holidays]);

    // Check if date should be highlighted
    const shouldHighlight = (date) => {
        return (isWeekend(date) || isHoliday(date)) && dayjs(date).isAfter(today);
    };

    // Set queue days
    const setQueueDays = useCallback((days) => {
        let weekendDays = 0;
        let holidays = 0;
        for (let i = 1; i <= days; i++) {
            const day = today.add(i, 'day');
            if (isWeekend(day.toDate())) {
                weekendDays++;
            }
            else if (isHoliday(day.toDate())) {
                holidays++;
            }
        }

        const queueDays = today.add(days + weekendDays + holidays, 'day');
        return queueDays;
    }, [today, isWeekend, isHoliday]);

    const queueDays = setQueueDays(queueSize);

    const beforeValidDate = (date) => (dayjs(date).isBefore(queueDays) && !(isWeekend(date) || isHoliday(date)));

    useEffect(() => {
        setMonth(setQueueDays(queueSize).toDate());
    },[queueSize])

    // Handler for selecting a date
    const handleSelectDate = (date) => {
        if (date) {
            dispatch(setDraft({ date: dayjs(date).format('YYYY-MM-DD') }));
        } else {
            dispatch(setDraft({ date: null }));
        }
    };

    return (
        <Card sx={{...workshopCardStyle, boxShadow:"none", border:"1px solid lightgray"}} className="workshop-calendar" >
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', marginBottom: '20px' }} className="workshop-calendar-header" textAlign={'center'}>
                {t("workshopCalendar.selectDate")}
            </Typography>
            <Box sx={{ width:"80%", display:"flex", justifyContent:"flex-end", margin:"0 auto" }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCurrentMonth}
                    disabled={disableButton}
                    sx={{
                        textTransform: "none",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#fff",
                        borderColor: !disableButton && "#003C59",
                        backgroundColor: !disableButton && "#003C59",
                    }}
                    >
                    {t("workshopCalendar.currentMonth")}
                </Button>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <DayPicker
                    animate
                    mode="single"
                    startMonth={currentMonth}
                    month={month}
                    onMonthChange={setMonth}    
                    selected={draft.date ? dayjs(draft.date).toDate() : undefined}
                    onSelect={handleSelectDate}
                    locale={calendarLocale}
                    disabled={[
                        { before: queueDays.toDate() },
                        isWeekend,
                        isHoliday
                    ]}
                    modifiers={{
                        highlightedDays: shouldHighlight,
                        disabledDays: beforeValidDate
                    }}
                    modifiersClassNames={{
                        highlightedDays: 'highlighted-days',
                        disabledDays: 'disabled-days'
                    }}
                    numberOfMonths={ (isDesktop || isTablet) ? 2 : 1}
                />
            </Box>
        </Card>
    );
}
